import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, EyeIcon, EyeOffIcon, ShieldCheck, Info } from "lucide-react";
import { Button, Spinner, Input } from "../../components";
import { UserService } from "../../services/UserService";
import { validatePassword, calculatePasswordStrength, isValidEmail } from "../../utils/validators";
import PasswordRequirements from "./components/PasswordRequirements";
import { detectPartnerFromHost } from "../../partner/detect";

/**
 * Reset Password Page
 * -------------------
 * With a valid token given in the URL, the user can reset their password.
 *  NOTE: This is also the component used to activate new accounts form the route /activate-account
 * See the route in the app/DistributorDashboard.tsx file.
 */

export const ResetPasswordPage: React.FC = () => {

  /********** STATE VARIABLES **********/
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const [isTokenInvalid, setIsTokenInvalid] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  /********** CONSTANTS **********/
  const userService = new UserService();
  const navigate = useNavigate();
  const location = useLocation();

  const partnerPreset = detectPartnerFromHost();
  const loginImage = partnerPreset.loginImgHref;

  /********** USE EFFECTS **********/
  useEffect(() => {
    const fetchUser = async () => {

      // If the user has a valid token to reset their password, they can. Otherwise, redirect to login.
      const queryParams = new URLSearchParams(location.search);
      const token = queryParams.get("token");
      if (token) {
        setToken(token);

        try {
          const tokenValidationResult = await userService.getUserByToken(token);
          if (!tokenValidationResult) {
            setIsTokenInvalid(true);
            return;
          }
  
          // Treat 204 (No Content) as invalid/expired
          if (tokenValidationResult.status === 204) {
            setIsTokenInvalid(true);
            return;
          }

          const isValid =
            tokenValidationResult.status === 200 ||
            tokenValidationResult.data?.success === true;
  
          if (!isValid) {
            setIsTokenInvalid(true);
            return;
          }
        } catch (error) {
          console.error("Failed to fetch user:", error);
          // If something went wrong validating, allow user to request a new link
          setIsTokenInvalid(true);
        }
      } else {
        // No token present; allow requesting a new reset email
        setIsTokenInvalid(true);
      }
    };

    fetchUser();
  }, [location, navigate]);

  // Calculate password strength
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  /********** HANDLERS **********/
  const handleBackToLogin = () => navigate("/login");

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);

    if (!newPassword) {
      setPasswordError("Password is required");
    } else if (!validatePassword(newPassword)) {
      setPasswordError("Password must be at least 8 characters and include uppercase, lowercase, and a number.");
    } else {
      setPasswordError(null);
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);

    if (newConfirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError(null);
    }
  };

  const handleResetPassword = async () => {
    if (!validatePassword(password)) {
      setPasswordError("Password must be at least 8 characters and include uppercase, lowercase, and a number");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      await userService.updatePassword({
        token: token,
        password: password,
      });
      setResetSuccess(true);
    } catch (error) {
      console.error("Password reset failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleResetPassword();
    }
  };

  /********** EXPIRED LINK EMAIL HANDLERS **********/
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = () => {
    if (email.trim() && !isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
    }
  };

  const handleSubmitEmail = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      setEmailError("Email address is required");
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    try {
      await userService.sendForgotPasswordEmail(email);
      setIsEmailSent(true);
    } catch (error) {
      console.error("Failed to send password reset email:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmitEmail();
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="w-2/5 relative flex items-center justify-center p-8 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginImage})` }}
      >
      </div>

      {/* Right Side - 60% */}
      <div className="w-3/5 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-md space-y-8">

          {/* Reset Success */}
          {resetSuccess ? (
            <>
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-100 rounded-full p-3">
                    <ShieldCheck size={40} className="text-green-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Success!
                </h2>
                <p className="text-gray-600">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
              </div>

              <div className="flex flex-col gap-4 items-center">
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={handleBackToLogin}
                  tabIndex={0}
                  aria-label="Back to Login"
                >
                  Return to Login
                </Button>
              </div>
            </>
          ) : isTokenInvalid ? (
            <>
              {!isEmailSent ? (
                <>
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Your reset link has expired.
                    </h2>
                    <p className="text-gray-600">
                      For security reasons, password reset links only work for a short time.
                    </p>
                    <p className="text-gray-600 mt-2">
                      You can request a new one below.
                    </p>
                  </div>

                  <form onSubmit={handleSubmitEmail} className="flex flex-col gap-4 items-center">
                    <div className="w-full">
                      <Input
                        type="email"
                        label="Email Address"
                        placeholder="Enter your email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        onKeyDown={handleEmailKeyDown}
                        required
                        aria-label="Email address input"
                        tabIndex={0}
                        error={emailError}
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full mt-4"
                      tabIndex={0}
                      aria-label="Send reset email"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleSubmitEmail(e as any);
                        }
                      }}
                      disabled={isSubmitting || !email.trim() || !isValidEmail(email)}
                    >
                      {isSubmitting ? "Sending" : "Send reset email"}
                      {isSubmitting && <Spinner className="ml-2" />}
                    </Button>

                    <div className="text-sm">
                      <button
                        type="button"
                        className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                        tabIndex={0}
                        aria-label="Back to Login"
                        onClick={handleBackToLogin}
                      >
                        Back to Login
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">
                      Check Your Inbox
                    </h2>
                    <p className="text-gray-600 mb-4">
                      We've sent a password reset link to <strong>{email}</strong>
                    </p>
                  </div>

                  <div className="flex flex-col gap-4 items-center">
                    <p className="text-sm text-gray-500">
                      Didn't receive the email? Check your spam folder or try again.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full"
                      onClick={() => {
                        setIsEmailSent(false);
                        setEmail("");
                        setEmailError("");
                      }}
                    >
                      Send email again
                    </Button>

                    <div className="text-sm">
                      <button
                        type="button"
                        className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                        tabIndex={0}
                        aria-label="Back to Login"
                        onClick={handleBackToLogin}
                      >
                        Back to Login
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Reset Your Password
                </h2>
                <p className="text-gray-600">
                  Enter your new password below
                </p>
              </div>

              <div className="flex flex-col gap-4">
                {/* Password Field */}
                <div className="relative">
                  <label htmlFor="password" className="text-sm font-medium text-gray-700 mb-1 flex items-center">
                    New Password
                    <Info
                      size={13}
                      className="ml-1 cursor-help"
                      onMouseEnter={() => setShowRequirements(true)}
                      onMouseLeave={() => setShowRequirements(false)}
                    />
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        id="password"
                        placeholder="Enter your new password"
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyDown={handleKeyDown}
                        aria-invalid={!!passwordError}
                        tabIndex={0}
                        className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${passwordError ? "border-red-500 focus:ring-red-500" : ""
                          }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        tabIndex={0}
                      >
                        {showPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                      </button>
                    </div>
                    {passwordError && <small className="mt-1 text-xs text-red-500 block">{passwordError}</small>}
                  </div>

                  {/* Password Strength Indicator */}
                  {password && (
                    <div className="mt-2">
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ease-out ${passwordStrength === 0
                              ? "bg-transparent"
                              : passwordStrength < 25
                                ? "bg-red-500"
                                : passwordStrength < 50
                                  ? "bg-orange-500"
                                  : passwordStrength < 70
                                    ? "bg-yellow-500"
                                    : passwordStrength < 90
                                      ? "bg-green-500"
                                      : "bg-emerald-500"
                            }`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span
                          className={`text-xs text-red-500 ${passwordStrength > 0 && passwordStrength < 25 ? "font-bold" : ""
                            }`}
                        >
                          Weak
                        </span>
                        <span
                          className={`text-xs text-orange-500 ${passwordStrength >= 25 && passwordStrength < 50 ? "font-bold" : ""
                            }`}
                        >
                          Fair
                        </span>
                        <span
                          className={`text-xs text-yellow-500 ${passwordStrength >= 50 && passwordStrength < 70 ? "font-bold" : ""
                            }`}
                        >
                          Good
                        </span>
                        <span
                          className={`text-xs text-green-500 ${passwordStrength >= 70 && passwordStrength < 90 ? "font-bold" : ""
                            }`}
                        >
                          Strong
                        </span>
                        <span className={`text-xs text-emerald-500 ${passwordStrength >= 90 ? "font-bold" : ""}`}>
                          Very Strong
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Password requirements component */}
                  <div className="relative">
                    <div
                      className={`absolute top-2 left-0 z-10 shadow-lg rounded-lg w-64 transition-opacity duration-150 ${showRequirements ? "opacity-100" : "opacity-0 pointer-events-none"
                        }`}
                    >
                      <PasswordRequirements password={password} passwordStrength={passwordStrength} />
                    </div>
                  </div>
                </div>

                {/* Confirm password */}
                <div className="relative">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        onKeyDown={handleKeyDown}
                        aria-invalid={!!confirmPasswordError}
                        tabIndex={0}
                        className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${confirmPasswordError ? "border-red-500 focus:ring-red-500" : ""
                          }`}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        tabIndex={0}
                      >
                        {showConfirmPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
                      </button>
                    </div>
                    {confirmPasswordError && <small className="mt-1 text-xs text-red-500 block">{confirmPasswordError}</small>}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  className="w-full mt-4"
                  onClick={handleResetPassword}
                  disabled={!!passwordError || !!confirmPasswordError || !password || !confirmPassword || isLoading}
                  tabIndex={0}
                  aria-label="Reset password button"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleResetPassword();
                    }
                  }}
                >
                  {isLoading ? (
                    <>
                      Resetting Password
                      <Spinner className="ml-2" />
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <div className="text-sm">
                  <button
                    type="button"
                    className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline flex items-center justify-center w-full"
                    onClick={handleBackToLogin}
                    tabIndex={0}
                    aria-label="Back to Login"
                  >
                    <ArrowLeft size="16" className="mr-2" /> Back to Login
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
